import React from 'react';
import { AppShell, Group, Title, Text, ActionIcon, Menu, Avatar, rem } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconUser,
  IconSettings,
  IconLogout,
  IconBell,
  IconMail,
  IconChevronDown,
  IconMenu2,
  IconHeart,
  IconSchool,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useUserProfile, usePermissions } from '../../hooks/useAuth';
import { useParentNotifications, useAllChildrenActivities } from '../../hooks/useRealtimeUpdates';
import { RealtimeIndicator } from '../common/RealtimeIndicator';
import ParentSidebar from './ParentSidebar';
import { ParentErrorBoundary } from '../common/ParentErrorBoundary';

interface ParentLayoutProps {
  children: React.ReactNode;
}

export const ParentLayout: React.FC<ParentLayoutProps> = ({ children }) => {
  const [opened, { toggle }] = useDisclosure();
  const { logout, isAuthenticated } = useAuth();
  const { getDisplayName, getInitials, role } = useUserProfile();
  const { isParent, isViewer } = usePermissions();
  const navigate = useNavigate();

  // Enable real-time updates for parent
  useParentNotifications();
  useAllChildrenActivities();

  // Redirect if not authenticated or not a parent/viewer
  React.useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    } else {
      const isParentUser = isParent() || isViewer();
      if (!isParentUser) {
        navigate('/unauthorized');
      }
    }
  }, [isAuthenticated, navigate]); // Remove isParent from dependencies

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (_error) {
      // Logout failed
    }
  };

  return (
    <AppShell
      header={{ height: 70 }}
      navbar={{
        width: 280,
        breakpoint: 'sm',
        collapsed: { mobile: !opened },
      }}
      padding="md"
    >
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between">
          <Group>
            <ActionIcon
              variant="outline"
              hiddenFrom="sm"
              onClick={toggle}
              size="lg"
            >
              <IconMenu2 size={18} />
            </ActionIcon>

            <Group gap="sm">
              <IconHeart size={28} color="var(--mantine-color-pink-6)" />
              <div>
                <Title order={3} fw={700} style={{ lineHeight: 1 }}>
                  YochienAI
                </Title>
                <Text size="xs" c="dimmed" fw={500} style={{ lineHeight: 1 }}>
                  Parent Portal
                </Text>
              </div>
            </Group>
          </Group>

          <Group gap="sm">
            {/* Real-time Status */}
            <RealtimeIndicator showText />

            {/* Notifications */}
            <ActionIcon variant="subtle" size="lg">
              <IconBell size={20} />
            </ActionIcon>

            {/* Messages */}
            <ActionIcon variant="subtle" size="lg">
              <IconMail size={20} />
            </ActionIcon>

            {/* User Menu */}
            <Menu shadow="md" width={200} position="bottom-end">
              <Menu.Target>
                <Group style={{ cursor: 'pointer' }} gap="xs">
                  <Avatar size="sm" radius="xl" color="pink">
                    {getInitials()}
                  </Avatar>
                  <div style={{ lineHeight: 1 }}>
                    <Text size="sm" fw={500}>
                      {getDisplayName()}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {role?.label || 'Parent Account'}
                    </Text>
                  </div>
                  <IconChevronDown size={14} />
                </Group>
              </Menu.Target>

              <Menu.Dropdown>
                <Menu.Label>Account</Menu.Label>
                <Menu.Item
                  leftSection={<IconUser style={{ width: rem(14), height: rem(14) }} />}
                  onClick={() => navigate('/parent/profile')}
                >
                  My Profile
                </Menu.Item>
                <Menu.Item
                  leftSection={<IconSettings style={{ width: rem(14), height: rem(14) }} />}
                  onClick={() => navigate('/parent/settings')}
                >
                  Settings
                </Menu.Item>
                <Menu.Item
                  leftSection={<IconBell style={{ width: rem(14), height: rem(14) }} />}
                  onClick={() => navigate('/parent/notifications')}
                >
                  Notifications
                </Menu.Item>

                <Menu.Divider />

                <Menu.Item
                  leftSection={<IconSchool style={{ width: rem(14), height: rem(14) }} />}
                  onClick={() => navigate('/parent/contact-school')}
                >
                  Contact School
                </Menu.Item>

                <Menu.Divider />

                <Menu.Item
                  leftSection={<IconLogout style={{ width: rem(14), height: rem(14) }} />}
                  color="red"
                  onClick={handleLogout}
                >
                  Sign Out
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="md" style={{ overflow: 'hidden' }}>
        <ParentSidebar />
      </AppShell.Navbar>

      <AppShell.Main>
        <ParentErrorBoundary>
          <div style={{ maxWidth: '100%', margin: '0 auto' }}>
            {children}
          </div>
        </ParentErrorBoundary>
      </AppShell.Main>
    </AppShell>
  );
};

export default ParentLayout;
